import { Service } from '@angular/core';
import { HttpResponse } from '@angular/common/http';

import { Logger } from '../logger.service';
import { dayjs } from '@app/shared/dayjs-setup';

const log = new Logger('HttpCacheService');
const cachePersistenceKey = 'httpCache';

export interface HttpCacheEntry {
  lastUpdated: Date;
  expirationDate: Date;
  data: HttpResponse<unknown>;
}

/**
 * Provides a cache facility for HTTP requests with configurable persistence policy.
 */
@Service({ autoProvided: false })
export class HttpCacheService {
  private cachedData: { [key: string]: HttpCacheEntry } = {};
  private storage: Storage | null = null;

  constructor() {
    this.cleanCache(undefined, true);
    this.loadCacheData();
  }

  /**
   * Sets the cache data for the specified request.
   * @param {!string} url The request URL.
   * @param {ResponseOptions} data The received data.
   * @param {Date=} lastUpdated The cache last update, current date is used if not specified.
   */
  setCacheData(url: string, data: HttpResponse<unknown>, lastUpdated?: Date, expirationDate?: Date) {
    Reflect.set(this.cachedData, url, {
      lastUpdated: lastUpdated || new Date(),
      expirationDate:
        expirationDate ||
        dayjs()
          .add(60 * 24, 'minute')
          .toDate(),
      data,
    });

    try {
      log.info(`Cache set for key: "${url}"`);
      this.saveCacheData();
    } catch (e) {
      log.info(`Clear cache for key: "${url}"`);
      this.clearCache(url);
    }
  }

  /**
   * Gets the cached data for the specified request.
   * @param {!string} url The request URL.
   * @return {?ResponseOptions} The cached data or null if no cached data exists for this request.
   */
  getCacheData(url: string): HttpResponse<unknown> | null {
    const cacheEntry = Reflect.get(this.cachedData, url);

    if (cacheEntry) {
      if (dayjs().isAfter(dayjs(cacheEntry.expirationDate))) {
        this.clearCache(url);
        return null;
      }

      log.info(`Cache hit for key: "${url}"`);
      return cacheEntry.data;
    }

    return null;
  }

  /**
   * Gets the cached entry for the specified request.
   * @param {!string} url The request URL.
   * @return {?HttpCacheEntry} The cache entry or null if no cache entry exists for this request.
   */
  getHttpCacheEntry(url: string): HttpCacheEntry | null {
    return Reflect.get(this.cachedData, url) || null;
  }

  /**
   * Clears the cached entry (if exists) for the specified request.
   * @param {!string} url The request URL.
   */
  clearCache(url: string): void {
    Reflect.deleteProperty(this.cachedData, url);
    log.info(`Cache cleared for key: "${url}"`);
    this.saveCacheData();
  }

  /**
   * Cleans cache entries older than the specified date.
   * @param {date=} expiration The cache expiration date. If no date is specified, all cache is cleared.
   */
  cleanCache(expiration?: Date, removeExpired: boolean = false) {
    if (expiration) {
      /*each(this.cachedData, (value: HttpCacheEntry, key: string) => {
        if (expirationDate >= value.lastUpdated) {
          delete this.cachedData[key];
        }
      });*/
    } else {
      if (removeExpired === true) {
        for (let index = 0; index < Object.keys(this.cachedData).length; index++) {
          const element: HttpCacheEntry = Reflect.get(this.cachedData, index);
          if (element.expirationDate <= new Date()) {
            Reflect.deleteProperty(this.cachedData, index);
          }
        }
      } else {
        this.cachedData = {};
      }
    }
    this.saveCacheData();
  }

  /**
   * Sets the cache persistence policy.
   * Note that changing the cache persistence will also clear the cache from its previous storage.
   * @param {'local'|'session'=} persistence How the cache should be persisted, it can be either local or session
   *   storage, or if no value is provided it will be only in-memory (default).
   */
  setPersistence(persistence?: 'local' | 'session') {
    // this.cleanCache();
    this.storage =
      persistence === 'local' || persistence === 'session'
        ? window[persistence === 'local' ? 'localStorage' : 'sessionStorage']
        : null;
    this.loadCacheData();
  }

  private saveCacheData() {
    if (this.storage) {
      Reflect.set(this.storage, cachePersistenceKey, JSON.stringify(this.cachedData));
    }
  }

  private loadCacheData() {
    const data = this.storage ? Reflect.get(this.storage, cachePersistenceKey) : null;
    this.cachedData = data ? JSON.parse(data) : {};
  }
}
